import { format, addDays, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';
import { enUS, he } from 'date-fns/locale';

/**
 * Get the schedule for a specific day from the flexible configuration
 * @param {Object} timeSlotConfig - Time slot configuration
 * @param {Date} date - Date object
 * @returns {Object|null} Day schedule or null if closed
 */
const getDaySchedule = (timeSlotConfig, date) => {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const daySchedules = timeSlotConfig.daySchedules || {};

  // Map day numbers to day names
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek];

  console.log('[getDaySchedule] Looking for day:', dayName, 'in schedules:', Object.keys(daySchedules));

  // First check for individual day schedule by name
  if (daySchedules[dayName]) {
    console.log('[getDaySchedule] Found schedule for', dayName, ':', daySchedules[dayName]);
    return daySchedules[dayName];
  }

  // Then check for numeric day override (0-6)
  if (daySchedules[dayOfWeek]) {
    return daySchedules[dayOfWeek];
  }

  // Backward compatibility: Check for "weekdays" grouping (Mon-Thu)
  if (dayOfWeek >= 1 && dayOfWeek <= 4 && daySchedules.weekdays) {
    return daySchedules.weekdays;
  }

  // Return default if no specific schedule found
  return {
    enabled: true,
    startTime: '0900',
    endTime: '1700',
    slotDuration: timeSlotConfig.defaultSlotDuration || 60,
    slotCapacity: timeSlotConfig.defaultCapacity || 20
  };
};

/**
 * Parse time string to minutes
 * @param {string} timeStr - Time in "HHMM" or "HH:MM" format
 * @returns {number} Minutes since midnight
 */
const parseTimeToMinutes = (timeStr) => {
  const cleanTime = timeStr.replace(':', '');
  const hours = parseInt(cleanTime.slice(0, 2));
  const minutes = parseInt(cleanTime.slice(2, 4)) || 0;
  return hours * 60 + minutes;
};

/**
 * Format minutes to time string
 * @param {number} minutes - Minutes since midnight
 * @returns {string} Time in "HH:MM" format
 */
const formatMinutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Generate time slots for a specific date based on event schedule configuration
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @param {string} selectedDate - Date in YYYY-MM-DD format
 * @returns {Array} Array of time slot objects
 */
export const generateTimeSlotsForDate = (event, selectedDate) => {
  // Handle new flexible configuration
  if (event?.timeSlotConfig?.enabled) {
    const { timeSlotConfig } = event;
    const dateObj = parseISO(selectedDate);
    const dateStr = format(dateObj, 'yyyy-MM-dd');

    // Check for date-specific overrides
    const dateOverride = timeSlotConfig.dateOverrides?.find(override => override.date === dateStr);
    if (dateOverride) {
      if (!dateOverride.schedule) return []; // Closed on this date
      // Use override schedule
      const schedule = dateOverride.schedule;
      return generateSlotsFromSchedule(schedule, selectedDate);
    }

    // Get the schedule for this day of week
    const daySchedule = getDaySchedule(timeSlotConfig, dateObj);
    if (!daySchedule?.enabled) return [];

    return generateSlotsFromSchedule(daySchedule, selectedDate);
  }

  // Fallback to old configuration for backward compatibility
  if (!event?.scheduleConfig || event.scheduleConfig.type !== 'DAILY_RECURRING') {
    return [];
  }

  const { dailySchedule, exceptions = [] } = event.scheduleConfig;

  // Check if date has exceptions
  const exception = exceptions.find(ex => ex.date === selectedDate);
  if (exception?.type === 'CLOSED') return [];

  // Use exception config or default daily schedule
  const config = exception?.type === 'MODIFIED' ? exception : dailySchedule;

  // Generate hourly slots
  const slots = [];
  const startHour = parseInt(config.startTime.split(':')[0]);
  const endHour = parseInt(config.endTime.split(':')[0]);

  for (let hour = startHour; hour < endHour; hour++) {
    const slotId = `${selectedDate}_${hour.toString().padStart(2, '0')}00`;
    const startTime = `${hour.toString().padStart(2, '0')}:00`;
    const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;

    slots.push({
      id: slotId,
      date: selectedDate,
      startTime: startTime,
      endTime: endTime,
      startDateTime: `${selectedDate}T${startTime}:00`,
      endDateTime: `${selectedDate}T${endTime}:00`,
      maxCapacity: config.maxCapacity || dailySchedule.maxCapacity,
      bookedCount: 0, // Will be updated from API
      isAvailable: true,
      availableSpots: config.maxCapacity || dailySchedule.maxCapacity
    });
  }

  // Ensure slots are sorted by start time (for RTL language layouts)
  return slots.sort((a, b) => {
    const aHour = parseInt(a.startTime.split(':')[0]);
    const bHour = parseInt(b.startTime.split(':')[0]);
    return aHour - bHour;
  });
};

/**
 * Generate slots from a schedule configuration
 * @param {Object} schedule - Schedule configuration with startTime, endTime, etc.
 * @param {string} selectedDate - Date in YYYY-MM-DD format
 * @returns {Array} Array of time slot objects
 */
const generateSlotsFromSchedule = (schedule, selectedDate) => {
  const slots = [];
  const startMinutes = parseTimeToMinutes(schedule.startTime);
  const endMinutes = parseTimeToMinutes(schedule.endTime);
  const slotDuration = schedule.slotDuration || 60;
  const slotCapacity = schedule.slotCapacity || 20;
  const breakTimes = schedule.breakTimes || [];

  // Check if selected date is today - filter out past slots
  const now = new Date();
  const todayString = format(now, 'yyyy-MM-dd');
  const isToday = selectedDate === todayString;
  const currentTimeMinutes = isToday ? (now.getHours() * 60 + now.getMinutes()) : 0;
  let currentMinutes = startMinutes;

  while (currentMinutes < endMinutes) {
    const slotEndMinutes = currentMinutes + slotDuration;

    // Skip slots that have already ended (check end time, not start time)
    // This allows booking ongoing slots that haven't finished yet
    if (isToday && slotEndMinutes <= currentTimeMinutes) {
      currentMinutes = slotEndMinutes;
      continue;
    }

    // Check if this slot overlaps with any break time
    const isBreak = breakTimes.some(breakTime => {
      const breakStart = parseTimeToMinutes(breakTime.startTime);
      const breakEnd = parseTimeToMinutes(breakTime.endTime);
      return (currentMinutes >= breakStart && currentMinutes < breakEnd) ||
             (slotEndMinutes > breakStart && slotEndMinutes <= breakEnd);
    });

    if (!isBreak && slotEndMinutes <= endMinutes) {
      const startTime = formatMinutesToTime(currentMinutes);
      const endTime = formatMinutesToTime(slotEndMinutes);
      const slotId = `${selectedDate}_${startTime.replace(':', '')}`;

      slots.push({
        id: slotId,
        date: selectedDate,
        startTime: startTime,
        endTime: endTime,
        startDateTime: `${selectedDate}T${startTime}:00`,
        endDateTime: `${selectedDate}T${endTime}:00`,
        maxCapacity: slotCapacity,
        bookedCount: 0,
        isAvailable: true,
        availableSpots: slotCapacity,
        slotDuration
      });
    }

    currentMinutes = slotEndMinutes;
  }

  return slots;
};

/**
 * Check if a date is bookable based on schedule configuration
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {boolean} Whether the date is bookable
 */
export const isDateBookable = (event, date) => {
  const selectedDate = parseISO(date);
  const today = startOfDay(new Date());

  // Check if date is in the past (compare dates only, not times)
  if (isBefore(selectedDate, today)) {
    return false;
  }

  // Check if date is in season (compare dates only, ignore time component)
  if (event.start_date && isBefore(selectedDate, startOfDay(parseISO(event.start_date)))) {
    console.log('[isDateBookable] Date is before season start - blocking');
    return false;
  }
  if (event.end_date && isAfter(selectedDate, startOfDay(parseISO(event.end_date)))) {
    return false;
  }

  // Handle new flexible configuration
  if (event?.timeSlotConfig?.enabled) {
    const { timeSlotConfig } = event;
    const bookingWindow = timeSlotConfig.bookingWindow || {};

    // Check advance booking window
    if (bookingWindow.advanceBookingDays) {
      const maxBookingDate = addDays(today, bookingWindow.advanceBookingDays);
      if (isAfter(selectedDate, maxBookingDate)) return false;
    }

    // Check date-specific overrides
    const dateOverride = timeSlotConfig.dateOverrides?.find(override => override.date === date);
    if (dateOverride) {
      return dateOverride.schedule !== null; // null means closed
    }

    // Check recurring exceptions
    if (timeSlotConfig.recurringExceptions) {
      for (const exception of timeSlotConfig.recurringExceptions) {
        if (exception.dayOfMonth === selectedDate.getDate()) {
          if (exception.closed) return false;
        }
        // Add more recurring exception logic as needed
      }
    }

    // Check day schedule
    const daySchedule = getDaySchedule(timeSlotConfig, selectedDate);
    return daySchedule?.enabled === true;
  }

  // Fallback to old configuration
  if (!event?.scheduleConfig) return false;

  const { dailySchedule, exceptions = [], bookingRules = {} } = event.scheduleConfig;

  // Check advance booking window
  if (bookingRules.advanceBookingDays) {
    const maxBookingDate = addDays(today, bookingRules.advanceBookingDays);
    if (isAfter(selectedDate, maxBookingDate)) return false;
  }

  // Check operating days (1-7 for Monday-Sunday)
  if (dailySchedule.operatingDays) {
    const dayOfWeek = selectedDate.getDay() || 7; // Convert Sunday from 0 to 7
    if (!dailySchedule.operatingDays.includes(dayOfWeek)) return false;
  }

  // Check exceptions
  const exception = exceptions.find(ex => ex.date === date);
  if (exception?.type === 'CLOSED') return false;

  return true;
};

/**
 * Get the next available booking date
 * @param {Object} event - Event object with scheduleConfig or timeSlotConfig
 * @returns {string} Next available date in YYYY-MM-DD format
 */
export const getNextAvailableDate = (event) => {
  let checkDate = new Date();
  let maxDays = 30; // default

  // Get max days from appropriate config
  if (event?.timeSlotConfig?.enabled) {
    maxDays = event.timeSlotConfig.bookingWindow?.advanceBookingDays || 30;
  } else if (event?.scheduleConfig) {
    maxDays = event.scheduleConfig.bookingRules?.advanceBookingDays || 30;
  } else {
    return format(new Date(), 'yyyy-MM-dd');
  }

  for (let i = 0; i < maxDays; i++) {
    const dateString = format(checkDate, 'yyyy-MM-dd');
    if (isDateBookable(event, dateString)) {
      return dateString;
    }
    checkDate = addDays(checkDate, 1);
  }

  return format(new Date(), 'yyyy-MM-dd');
};

/**
 * Check if event has recurring time slots
 * @param {Object} event - Event object
 * @returns {boolean} Whether event has recurring time slots
 */
export const hasRecurringTimeSlots = (event) => {
  // Check for new flexible configuration
  if (event?.timeSlotConfig?.enabled) {
    return true;
  }
  // Fallback to old configuration for backward compatibility
  return event?.scheduleConfig?.type === 'DAILY_RECURRING';
};

/**
 * Update slot availability with booking data
 * @param {Array} slots - Generated time slots
 * @param {Array} bookings - Booking data from API (server format)
 * @returns {Array} Updated slots with availability
 */
export const updateSlotsWithBookings = (slots, bookings = []) => {
  return slots.map(slot => {
    // Find matching booking data from server response
    // Server returns format: { startTime: "0900", availableCount: 49, bookedCount: 1, ... }
    const serverSlot = bookings.find(booking => {
      // Handle both "0900" and "09:00" formats
      const serverTime = booking.startTime?.includes(':')
        ? booking.startTime.replace(':', '')
        : booking.startTime;
      const slotTime = slot.startTime?.includes(':')
        ? slot.startTime.replace(':', '')
        : slot.startTime;

      return serverTime === slotTime &&
        (!booking.date || booking.date === slot.date);
    });

    if (serverSlot) {
      // Use server data directly
      const availableSpots = serverSlot.availableCount !== undefined
        ? serverSlot.availableCount
        : (serverSlot.totalCapacity - serverSlot.bookedCount);

      return {
        ...slot,
        bookedCount: serverSlot.bookedCount || 0,
        availableSpots,
        isAvailable: availableSpots > 0,
        totalCapacity: serverSlot.totalCapacity || slot.maxCapacity,
        pendingCount: serverSlot.pendingCount || 0,
        // Keep server data for reference
        serverData: serverSlot
      };
    }

    // No booking data from server - slot is fully available
    return {
      ...slot,
      bookedCount: 0,
      availableSpots: slot.maxCapacity,
      isAvailable: true
    };
  });
};

/**
 * Format time slot for display
 * @param {Object} slot - Time slot object
 * @param {string} lang - Language code (optional)
 * @returns {string} Formatted time range
 */
export const formatTimeSlot = (slot, lang) => {
  // For Hebrew (RTL), show later time first
  if (lang === 'he') {
    return `${slot.endTime} - ${slot.startTime}`;
  }
  // For other languages (LTR), show earlier time first
  return `${slot.startTime} - ${slot.endTime}`;
};

// ============================================================================
// PURE HELPER FUNCTIONS
// ============================================================================

/**
 * Parse slot ID into its components
 * @pure
 */
const parseSlotId = (slotId) => {
  const [datePart, timePart] = slotId.split('_');
  return {
    datePart,
    hour: timePart.substring(0, 2),
    minute: timePart.substring(2, 4)
  };
};

/**
 * Find slot by ID in availableSlots array
 * @pure
 */
const findSlot = (slotId, slots) =>
  slots?.find(slot => slot.id === slotId) || null;

/**
 * Extract time slot data from slot object
 * @pure
 */
const extractTimeSlot = (slot) => ({
  startTime: slot.startTime,
  endTime: slot.endTime
});

/**
 * Get slot duration from event configuration
 * @pure
 */
const getSlotDuration = (event) => {
   return  event?.timeSlotConfig?.enabled
       ? event.timeSlotConfig.defaultSlotDuration || 60
       : event?.scheduleConfig?.dailySchedule?.slotDuration || 60;
}


/**
 * Calculate end time based on start time and duration
 * @pure
 */
const calculateEndTime = (hour, minute, duration) => {
  const totalMinutes = parseInt(hour) * 60 + parseInt(minute) + duration;
  const endHour = Math.floor(totalMinutes / 60);
  const endMin = totalMinutes % 60;
  return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
};

/**
 * Build time slot object from start and end times
 * @pure
 */
const buildTimeSlot = (startTime, endTime) => ({
  startTime,
  endTime
});

/**
 * Get locale for date formatting
 * @pure
 */
const getLocale = (lang) => lang === 'he' ? he : enUS;

/**
 * Format date with locale
 * @pure
 */
const formatDate = (datePart, locale) =>
  format(parseISO(datePart), 'EEEE, dd MMMM yyyy', { locale });

/**
 * Format complete date-time string
 * @pure
 */
const formatDateTime = (datePart, timeSlot, currentLang) => {
  const locale = getLocale(currentLang);
  const dateString = formatDate(datePart, locale);
  const timeString = formatTimeSlot(timeSlot, currentLang);
  return `${dateString} | ${timeString}`;
};

// ============================================================================
// COMPOSED FUNCTIONS (Primary & Fallback Paths)
// ============================================================================

/**
 * Attempt to format slot from availableSlots array (primary path)
 * @pure (except for early return pattern)
 * @returns {string | null} Formatted string or null if slot not found
 */
const attemptPrimaryPath = ({ selectedTimeSlot, availableSlots, currentLang }) => {
  // Find slot (returns null if not found)
  const slot = findSlot(selectedTimeSlot, availableSlots);

  // Early return if not found
  if (!slot) return null;

  // Transform and format
  const timeSlot = extractTimeSlot(slot);
  const { datePart } = parseSlotId(selectedTimeSlot);

  return formatDateTime(datePart, timeSlot, currentLang);
};

/**
 * Format slot using fallback calculation
 * @pure (except console.warn)
 * @returns {string} Formatted string
 */
const attemptFallbackPath = ({ selectedTimeSlot, event, currentLang }) => {
  console.warn('[timeSlots] Selected slot not found in availableSlots, using fallback calculation');

  const { datePart, hour, minute } = parseSlotId(selectedTimeSlot);
  const duration = getSlotDuration(event);
  const startTime = `${hour}:${minute}`;
  const endTime = calculateEndTime(hour, minute, duration);
  const timeSlot = buildTimeSlot(startTime, endTime);

  return formatDateTime(datePart, timeSlot, currentLang);
};

/**
 * Format selected time slot with date for display in checkout
 *
 * Uses pragmatic functional programming:
 * - Pure helper functions for all operations
 * - Early returns for null handling (simple and clear)
 * - Function composition for control flow
 * - Immutable data transformations
 *
 * @param {Object} options - Configuration options
 * @param {string} options.selectedTimeSlot - Slot ID (e.g., "2025-09-23_1300")
 * @param {string} options.selectedDate - Date string in YYYY-MM-DD format
 * @param {Array} options.availableSlots - Array of slot objects with startTime/endTime
 * @param {string} options.currentLang - Language code ('he' for Hebrew, otherwise LTR)
 * @param {Object} options.event - Event object (for fallback calculations)
 * @returns {string|null} Formatted date and time string, or null if invalid
 *
 * @example
 * formatSelectedSlotDateTime({
 *   selectedTimeSlot: "2025-09-23_1300",
 *   selectedDate: "2025-09-23",
 *   availableSlots: [...],
 *   currentLang: "en",
 *   event: {...}
 * })
 * // => "Tuesday, 23 September 2025 | 13:00 - 14:00"
 */
export const formatSelectedSlotDateTime = (options) => {
  const { selectedDate, selectedTimeSlot } = options;

  // Guard clause: early return for invalid input
  if (!selectedDate || !selectedTimeSlot) return null;

  try {
    // Try primary path (availableSlots lookup)
    const primaryResult = attemptPrimaryPath(options);

    // If found in availableSlots, return it
    if (primaryResult) return primaryResult;

    // Otherwise, use fallback calculation
    return attemptFallbackPath(options);

  } catch (err) {
    console.error('[timeSlots] Error formatting date/time:', err);
    // Ultimate fallback: return raw values
    return `${selectedDate} - ${selectedTimeSlot}`;
  }
};

/**
 * Get availability status for display
 * @param {Object} slot - Time slot object
 * @param {Function} translate - Translation function (optional)
 * @returns {Object} Status object with color, text, and translation key
 */
export const getAvailabilityStatus = (slot, translate) => {
  const { availableSpots, maxCapacity } = slot;
  const percentage = (availableSpots / maxCapacity) * 100;

  if (availableSpots === 0) {
    const text = translate ? translate('time_slots.fully_booked') : 'Fully Booked';
    return { color: 'error', text, variant: 'error', key: 'fully_booked' };
  } else if (percentage <= 20) {
    const text = translate ? translate('time_slots.only_x_left', { count: availableSpots }) : `Only ${availableSpots} left`;
    return { color: 'warning', text, variant: 'warning', key: 'limited_spots' };
  } else if (percentage <= 50) {
    const text = translate ? translate('time_slots.x_spots_left', { count: availableSpots }) : `${availableSpots} spots left`;
    return { color: 'info', text, variant: 'info', key: 'spots_available' };
  } else {
    const text = translate ? translate('time_slots.x_available', { count: availableSpots }) : `${availableSpots} available`;
    return { color: 'success', text, variant: 'success', key: 'many_available' };
  }
};